import React, { startTransition, useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CrosshairCanvas } from "../components/crosshairCanvas";
import { PageSeo } from "../components/PageSeo";
import { Pagination } from "../components/Pagination";
import { CrosshairCanvasDisplayEnum, ICrosshairConfig, IPageProps } from "../interfaces/interface";
import { crosshairsService } from "../services/crosshairsService";
import { favoritesService } from "../services/favoritesService";
import { useAuth } from "../contexts/AuthContext";
import { CrosshairCatalogSkeleton } from "../components/CrosshairCatalogSkeleton";
import { LoginModal } from "../components/LoginModal";
import { CrosshairFaq } from "../components/CrosshairFaq";
import { buildFaqPageJsonLd, getHomeFaqItems } from "../seo/faqContent";
import { buildItemListJsonLd, buildOrganizationJsonLd, buildWebSiteJsonLd } from "../seo/jsonLd";
import { pageSeoProps } from "../seo/pageMeta";
import { getUiStrings } from "../seo/uiStrings";
import { useLocale } from "../hooks/useLocale";
import { PageBody } from "../components/PageBody";
import { queuePendingFavoriteCrosshairId } from "../auth/pendingAuthActions";
import { usePendingFavoriteAfterAuth } from "../hooks/usePendingFavoriteAfterAuth";
import { useCatalogPage } from "../hooks/useCatalogPage";
import { IFavoriteResult } from "../services/favoritesService";

export function HomePage(props: IPageProps) {
    const { locale, pathname } = useLocale();
    const { user, loading: authLoading } = useAuth();
    const [crosshairs, setCrosshairs] = useState<ICrosshairConfig[]>([]);
    const [fetching, setFetching] = useState(true);
    const { page, buildHref } = useCatalogPage(props.searchValue);
    const [totalPages, setTotalPages] = useState(1);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [recentCommunity, setRecentCommunity] = useState<ICrosshairConfig[]>([]);
    const [recentCommunityLoading, setRecentCommunityLoading] = useState(true);

    const applyPendingFavorite = useCallback((id: number, result: IFavoriteResult) => {
        setCrosshairs((prev) =>
            prev.map((c) => (c.id === id ? { ...c, isFavorited: result.isFavorited, favoriteCount: result.favoriteCount } : c))
        );
        setRecentCommunity((prev) =>
            prev.map((c) => (c.id === id ? { ...c, isFavorited: result.isFavorited, favoriteCount: result.favoriteCount } : c))
        );
    }, []);

    usePendingFavoriteAfterAuth(authLoading, user, applyPendingFavorite);

    const handleFavoriteClick = async (id: number) => {
        if (authLoading) return;
        if (!user) {
            queuePendingFavoriteCrosshairId(id);
            setShowLoginModal(true);
            return;
        }
        try {
            const result = await favoritesService.toggle(id);
            applyPendingFavorite(id, result);
        } catch { }
    };

    useEffect(() => {
        setFetching(true);
        crosshairsService
            .getAll(props.searchValue || undefined, page)
            .then((r) => {
                startTransition(() => {
                    setCrosshairs(r.items);
                    setTotalPages(r.totalPages);
                    setFetching(false);
                });
            })
            .catch(() => setFetching(false));
    }, [props.searchValue, page]);

    useEffect(() => {
        setRecentCommunityLoading(true);
        crosshairsService
            .getCommunity(undefined, 1, 5)
            .then((r) => setRecentCommunity(r.items))
            .catch(() => setRecentCommunity([]))
            .finally(() => setRecentCommunityLoading(false));
    }, []);

    const jsonLd = useMemo(() => buildWebSiteJsonLd(), []);
    const organizationJsonLd = useMemo(() => buildOrganizationJsonLd(), []);
    const faqItems = useMemo(() => getHomeFaqItems(locale), [locale]);
    const faqJsonLd = useMemo(() => buildFaqPageJsonLd(faqItems), [faqItems]);
    const itemListJsonLd = useMemo(() => buildItemListJsonLd(crosshairs, locale), [crosshairs, locale]);

    const meta = pageSeoProps("/", locale);
    const ui = getUiStrings(locale);

    return (
        <React.Fragment>
            <PageSeo
                path={pathname}
                page={page}
                {...meta}
            />
            <Helmet>
                <script type="application/ld+json">{jsonLd}</script>
                <script type="application/ld+json">{organizationJsonLd}</script>
                <script type="application/ld+json">{faqJsonLd}</script>
                {crosshairs.length > 0 ? (
                    <script type="application/ld+json">{itemListJsonLd}</script>
                ) : null}
            </Helmet>
            {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
            {/* Heading and lead first, then the catalog. The page used to open with the newest
                community uploads — often a code-named entry with no likes — and put the H1
                below all thirty crosshairs, out of sight on a phone. */}
            <section className="main-content home-intro text-start">
                <h1>{meta.h1}</h1>
                <p>{meta.intro}</p>
            </section>

            <div className={`crosshair-canvas-previews${fetching ? " crosshair-canvas-previews--fetching" : ""}`}>
                {crosshairs.length > 0 ? crosshairs.map((item) => {
                    return <CrosshairCanvas key={item.id} displaySetting={CrosshairCanvasDisplayEnum.Preview} code={item.code} name={item.name} crosshairId={item.id} crosshairSlug={item.slug} favoriteCount={item.favoriteCount} isFavorited={item.isFavorited} onFavoriteClick={handleFavoriteClick}></CrosshairCanvas>
                }) : !fetching ? <div className="no-result-text">No result found</div> : <CrosshairCatalogSkeleton />}
            </div>
            <Pagination page={page} totalPages={totalPages} buildHref={buildHref} />

            <div className="home-add-crosshair-cta" role="region" aria-label="Add your crosshair">
                <p className="home-add-crosshair-cta__text">
                    <strong>Add your crosshair</strong> — create it in the generator, save it to your account, and after
                    review it can appear here for everyone.
                </p>
                <div className="home-add-crosshair-cta__actions">
                    <Link to="/valorant-crosshair-generator" className="btn btn-primary btn-sm home-add-crosshair-cta__btn">
                        Open generator
                    </Link>
                    <Link to="/my-crosshairs" className="btn btn-outline-light btn-sm home-add-crosshair-cta__btn">
                        My crosshairs
                    </Link>
                </div>
            </div>

            <section className="home-community-recent" aria-labelledby="home-community-recent-heading">
                <div className="home-community-recent__head">
                    <h2 id="home-community-recent-heading" className="home-community-recent__title">
                        Recently added by players
                    </h2>
                    <Link to="/valorant-community-crosshairs" className="btn btn-outline-light btn-sm home-community-recent__all">
                        View all community crosshairs
                    </Link>
                </div>
                <p className="home-community-recent__sub">
                    Approved submissions from signed-in users — same entries you’ll find on the{" "}
                    <Link to="/valorant-community-crosshairs">community</Link> page, newest first.
                </p>
                {recentCommunityLoading ? (
                    <div
                        className="crosshair-canvas-previews home-community-recent__previews"
                        aria-busy="true"
                        aria-label="Loading community crosshairs"
                    >
                        <CrosshairCatalogSkeleton count={5} />
                    </div>
                ) : recentCommunity.length > 0 ? (
                    <div className="crosshair-canvas-previews home-community-recent__previews">
                        {recentCommunity.map((item, index) => (
                            <CrosshairCanvas
                                key={item.id}
                                displaySetting={CrosshairCanvasDisplayEnum.Preview}
                                code={item.code}
                                name={item.name}
                                crosshairId={item.id} crosshairSlug={item.slug}
                                favoriteCount={item.favoriteCount}
                                isFavorited={item.isFavorited}
                                onFavoriteClick={handleFavoriteClick}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="home-community-recent__empty">
                        No player submissions in the catalog yet.{" "}
                        <Link to="/valorant-crosshair-generator">Create one in the generator</Link> and submit it from{" "}
                        <Link to="/my-crosshairs">My crosshairs</Link>.
                    </p>
                )}
            </section>

            <main className="main-content">
                {meta.body && (
                    <section className="text-start">
                        <PageBody blocks={meta.body} />
                    </section>
                )}

                {/* Rendered for every locale: the FAQPage schema above is emitted unconditionally,
                    so the questions must be on the page in every language too. */}
                <CrosshairFaq
                    idPrefix="home"
                    heading={ui.faq.homeHeading}
                    items={faqItems}
                />
                <div className="text-start">
                    <PageBody blocks={[{ p: ui.faq.homeMore }]} />
                </div>
            </main>
        </React.Fragment>
    );
}